import React, { useCallback, useMemo, useRef } from "react";
import Collapsible from "react-collapsible";
import { CareCommentThread } from "../../../domain/care_comment/CareCommentThread";
import { useBoolean } from "../../hooks/common/useBoolean";
import classNames from "classnames";
import { AddCareComment } from "./AddCareComment";
import { AdminOnlyComponents } from "../admin/components/AdminOnlyComponents";
import { CareCommentItem } from "./CareCommentItem";
import { useAppDispatch } from "../../../state/store";
import { markAsReadCareCommentAction, pinCareCommentAction, unpinCareCommentAction } from "../../../state/usecase/care_comment/actions";
import { SwitchButton } from "../common/button/SwitchButton";
import { useCareCommentThreadList } from "../../hooks/care_comment/useCareCommentThreadList";
import html2canvas from "html2canvas";
import { CameraIcon } from "../common/icons/CameraIcon";
import { useCareRecipientList } from "../../hooks/care_recipient/useCareRecipientList";
import { useCareRecipientIdFromPath } from "../../hooks/care_recipient/useCareRecipientIdFromPath";
import { Navigate } from "react-router-dom";
import { Paths } from "../../../Paths";

export const CareCommentThreadItem = React.memo(function CareCommentThreadItem(props: CareCommentThreadItemProps) {
    const dispatch = useAppDispatch();

    const careCommentThreadList = useCareCommentThreadList();

    const { careRecipients } = useCareRecipientList();

    const careRecipientId = useCareRecipientIdFromPath();

    if (!careRecipientId) {
        return <Navigate to={Paths.root} />;
    }

    const careRecipientName = useMemo(() => {
        const careRecipient = careRecipients.find(careRecipient => careRecipient.id.value === careRecipientId.value);
        return careRecipient?.name.value ?? "";
    }, [careRecipients, careRecipientId]);

    const pinnedCareCommentThreadList = useMemo(() => (
        careCommentThreadList ? careCommentThreadList.filter(careCommentThread => careCommentThread.isPinned) : []
    ), [careCommentThreadList]);

    const { careCommentThread } = props;

    const firstCareComment = useMemo(() => careCommentThread.careComments[0], [careCommentThread]);

    const readCareComments = useMemo(() => careCommentThread.careComments.filter(careComment => careComment.isRead), [careCommentThread]);

    const unreadCareComments = useMemo(() => careCommentThread.careComments.filter(careComment => !careComment.isRead), [careCommentThread]);

    const [open, , , toggleCareCommentOpen] = useBoolean();

    const onSwitchClick = useCallback(() => {
        const disabledSwitch = pinnedCareCommentThreadList ? pinnedCareCommentThreadList.length >= 100 : false;
        
        if (disabledSwitch && !careCommentThread.isPinned) return;

        if (careCommentThread.isPinned) {
            // 継続掲示状態でスイッチが押されたら解除する
            dispatch(
                unpinCareCommentAction(careCommentThread.id)
            );
        } else {
            // 継続掲示未設定状態でスイッチが押されたら設定する
            dispatch(
                pinCareCommentAction(careCommentThread.id)
            );
        }

    }, [pinnedCareCommentThreadList, careCommentThread, dispatch]);

    const onCareCommentThreadClicked = useCallback(() => {
        toggleCareCommentOpen();

        if (open && unreadCareComments.length) {
            dispatch(
                markAsReadCareCommentAction(unreadCareComments.map(careComment => careComment.id))
            );
        }
    }, [toggleCareCommentOpen, open, unreadCareComments, dispatch]);

    const careCommentThreadRef = useRef<HTMLDivElement>(null);

    const saveScreenShot = useCallback(() => {
        const target = careCommentThreadRef.current;

        // 撮影時のみ利用者名とQRの表示を追加
        const careRecipientNameElement = document.createElement("div");
        careRecipientNameElement.textContent = `${careRecipientName}様`;
        careRecipientNameElement.style.fontSize = "32px";
        careRecipientNameElement.style.fontWeight = "bold";
        target?.prepend(careRecipientNameElement);

        const qrCodeElements = target?.getElementsByClassName("rq_code");
        Array.from(qrCodeElements ?? []).forEach(qrCode => qrCode.setAttribute("style", "display: block;"));

        html2canvas(target!).then(canvas => {    
            const targetImgUri = canvas.toDataURL("img/png");

            const downloadLink = document.createElement("a");

            downloadLink.style.visibility = "hidden";
    
            if (typeof downloadLink.download === "string") {
                downloadLink.href = targetImgUri;
            
                // ファイル名
                downloadLink.download = `${firstCareComment.createdTime.format()}_${firstCareComment.title.value}`;
            
                // Firefox では body の中にダウンロードリンクがないといけないので一時的に追加
                document.body.appendChild(downloadLink);
            
                // ダウンロードリンクが設定された a タグをクリック
                downloadLink.click();
            
                // Firefox 対策で追加したリンクを削除しておく
                document.body.removeChild(downloadLink);
            } else {
                window.open(targetImgUri);
            }            
        });

        // 追加した利用者名・IDを削除
        target?.removeChild(careRecipientNameElement);
        Array.from(qrCodeElements ?? []).forEach(qrCode => qrCode.setAttribute("style", ""));
    }, [careRecipientName, firstCareComment]);

    return (
        <>
            {/* 継続掲示スイッチ */}
            <div className={"switch_button_area"}>
                <SwitchButton switchOn={careCommentThread.isPinned} onClick={onSwitchClick} />
                <span>継続掲示する</span>
            </div>
            {/* スクショ */}
            <div className={"screen_shot_button_area"}>
                <button onClick={saveScreenShot}>
                    <CameraIcon />
                </button>
                <div className={classNames("tooltip")}>
                    画像で保存
                </div>
            </div>
            {/* 内容 */}
            <Collapsible
                // ケアコメント情報をアコーディオンのトリガーにする
                trigger={
                    <div className={"care_comment_info"} onClick={onCareCommentThreadClicked}>
                        {/* 日時 */}
                        <div className={"time"}>
                            {`${careCommentThread.createdTime.date.japaneseMonthDateFormat()} ${careCommentThread.createdTime.time.formatHourMinute()}`}
                        </div>
                        {/* タイトル・内容 */}
                        <div className={"detail"}>
                            {/* タイトル */}
                            <div className={"title"}>
                                {firstCareComment.title.value}
                            </div>
                            {/* 開閉ボタン */}
                            <button className={"toggle_button"}>
                                {open ? "▲スレッドを閉じる" : "▼スレッドを見る"}
                            </button>
                        </div>
                    </div>
                }
                transitionTime={100}
            >
                <div className={classNames("care_comment_content", { "open": open })} ref={careCommentThreadRef}>
                    {/* ケアコメント（既読） */}
                    {readCareComments.map(careComment => <CareCommentItem key={careComment.id.value} careComment={careComment} />)}
                    {/* ケアコメント（未読） */}
                    {!!unreadCareComments.length && (
                        <>
                            <div className={"unread_comment_label"}>
                                <div>新着コメント</div>
                            </div>
                            {unreadCareComments.map(careComment => <CareCommentItem key={careComment.id.value} careComment={careComment} />)}
                        </>
                    )}
                    {/* 追加ボタン */}
                    <AdminOnlyComponents>
                        <AddCareComment careCommentThreadId={careCommentThread.id} />
                    </AdminOnlyComponents>
                </div>
            </Collapsible>
        </>
    );
});

type CareCommentThreadItemProps = {
    careCommentThread: CareCommentThread
}
